import React from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid2";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import ClearIcon from "@mui/icons-material/Clear";
import { translate } from "@flow-frontend-npm/common";
import { DateTimePicker } from "@flow-frontend-npm/date-pickers";
import { getPeriodOptions, getPeriodDates } from "./periods";

import t from "./translations.json";

const periodLabels = translate(t, "selectDateTimePeriod.periodOptions");

export const periods = [
    { label: periodLabels.today, value: "today" },
    { label: periodLabels.yesterday, value: "yesterday" },
    { label: periodLabels.week, value: "week" },
    { label: periodLabels.lastWeek, value: "lastWeek" },
    { label: periodLabels.month, value: "month" },
    { label: periodLabels.lastMonth, value: "lastMonth" },
    { label: periodLabels.custom, value: "custom" },
];

/**
 * Form input component to select DateTimePeriod
 * Preset date period can be selected, like today, yesterday, week, month...
 * Or custom date range can be set with DateTimePicker component
 * @param {object} inputStyle Common stlye definition for input fields
 * @param {object} value Period values
 * @param {function} onChange Emitted when a value changed
 * @param {object} gridItemBreakpoints Grid breakpoint definitions for the input fields
 * @param {string} idPrefix Id prefix for input elements
 * @param {number} spacing spacing between fields
 * @return {JSX.Element}
 */
const SelectDateTimePeriod = ({
    inputStyle,
    value,
    onChange,
    gridItemBreakpoints,
    idPrefix,
    spacing,
}) => {
    const defaultValue = {
        period: "",
        start: null,
        end: null,
    };

    const { period, start, end } = value || defaultValue;

    const handlePeriodSelectEvent = (event) => {
        if (event.target.value === "custom") {
            onChange({
                period: event.target.value,
                start,
                end,
            });
        } else {
            onChange({
                period: event.target.value,
                ...getPeriodDates(event.target.value),
            });
        }
    };

    const handlePeriodStartDateValueChange = (start) => {
        onChange({
            period: "custom",
            start,
            end,
        });
    };

    const handlePeriodEndDateValueChange = (end) => {
        onChange({
            period: "custom",
            start,
            end,
        });
    };

    return (
        <Grid container direction="row" spacing={spacing || 3}>
            <Grid size={gridItemBreakpoints}>
                <FormControl fullWidth sx={inputStyle}>
                    <InputLabel>
                        {translate(t, "selectDateTimePeriod.period")}
                    </InputLabel>
                    <Select
                        inputProps={{ id: idPrefix && `${idPrefix}Name` }}
                        label={translate(t, "selectDateTimePeriod.period")}
                        value={period}
                        onChange={handlePeriodSelectEvent}
                        endAdornment={
                            period && (
                                <InputAdornment
                                    position="end"
                                    sx={{ position: "absolute", right: "30px" }}
                                >
                                    <IconButton
                                        onClick={() => onChange(defaultValue)}
                                    >
                                        <ClearIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }
                    >
                        {getPeriodOptions().map(({ value, label }) => (
                            <MenuItem key={value} value={value}>
                                {label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid size={gridItemBreakpoints}>
                <DateTimePicker
                    label={translate(t, "selectDateTimePeriod.periodFrom")}
                    inputProps={{
                        sx: inputStyle,
                        id: idPrefix && `${idPrefix}Start`,
                    }}
                    value={start}
                    onChange={handlePeriodStartDateValueChange}
                />
            </Grid>
            <Grid size={gridItemBreakpoints}>
                <DateTimePicker
                    label={translate(t, "selectDateTimePeriod.periodTo")}
                    inputProps={{
                        sx: inputStyle,
                        id: idPrefix && `${idPrefix}End`,
                    }}
                    value={end}
                    onChange={handlePeriodEndDateValueChange}
                />
            </Grid>
        </Grid>
    );
};

SelectDateTimePeriod.propTypes = {
    inputStyle: PropTypes.object,
    onChange: PropTypes.func,
    periodType: PropTypes.string,
    value: PropTypes.object,
    gridItemBreakpoints: PropTypes.object,
    idPrefix: PropTypes.string,
    spacing: PropTypes.number,
};

export default SelectDateTimePeriod;
